export interface RenewalProgressLike {
  pagarme_last_order_id?: string | null;
  next_payment_at?: string | null;
}

const timeOf = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Whether our renewal webhook has applied a paid PIX order: the renewal clears
 * `pagarme_last_order_id` and moves `next_payment_at` forward. Pass `orderId`
 * only for an order stored as `pagarme_last_order_id` (renewal orders); a
 * wallet + PIX remainder order is never stored, so only the due date counts.
 */
export function isRenewalApplied({ orderId, before, after }: {
  orderId: string | null;
  before: RenewalProgressLike | null | undefined;
  after: RenewalProgressLike | null | undefined;
}): boolean {
  if (!after) return false;
  if (orderId && String(after.pagarme_last_order_id ?? '') !== String(orderId)) return true;
  const was = timeOf(before?.next_payment_at);
  const now = timeOf(after.next_payment_at);
  return was !== null && now !== null && now > was;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * The PIX status poll reads Pagar.me, which reports "paid" before our webhook
 * applies the renewal. Refetch the subscription every `intervalMs` (from right
 * away until `timeoutMs`) until `isRenewalApplied`. Returns whether it was
 * confirmed and the last subscription read; a failed refetch just waits for
 * the next one.
 */
export async function waitForRenewalApplied<S extends RenewalProgressLike>({
  orderId,
  before,
  refetch,
  intervalMs = 2000,
  timeoutMs = 20000,
  sleep = realSleep,
}: {
  orderId: string | null;
  before: RenewalProgressLike | null | undefined;
  refetch: () => Promise<S | null | undefined>;
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<{ applied: boolean; subscription: S | null }> {
  let latest: S | null = null;
  for (let waited = 0; ; waited += intervalMs) {
    try {
      latest = (await refetch()) ?? latest;
      if (isRenewalApplied({ orderId, before, after: latest })) {
        return { applied: true, subscription: latest };
      }
    } catch {
      // Offline or a server hiccup: try again on the next round.
    }
    if (waited + intervalMs > timeoutMs) return { applied: false, subscription: latest };
    await sleep(intervalMs);
  }
}
